// ============================================================================
// AuthService — session lifecycle + permission gating.
//
// Identity (who) comes from the auth mode (AppConfig.authMode):
//   mock — no backend; a fake Admin is signed in (default for dev/demo).
//   bff  — token-handler: full-page redirect to the BFF; session via httpOnly
//          cookie; identity from GET <bff.userinfo>. SPA never sees tokens.
//   oidc — direct Keycloak (Auth-Code + PKCE); tokens in sessionStorage (stub).
//
// Authorization (what they can do) comes from StaffService.WhoAmI when a live
// backend is reachable: a flat effective-permission set, ticket-visibility tier,
// superadmin flag, and whether AI assist is available. WhoAmI requires real auth
// (it is NOT mounted in the open-mode dev backend), so:
//   • authMode 'mock'        → synthesize a full-admin grant (perms ['*']);
//   • bff/oidc + WhoAmI ok   → use the tenant's effective permissions;
//   • bff/oidc + WhoAmI fail → fall back to the role→permission map.
// can() is the single gate the UI and route guards consult.
//
// Solid batches signal writes, so the session state is also mirrored in plain
// fields (`now`): the bootstrap and callback flows read what they just wrote.
// ============================================================================
import { navigate } from '~/lib/nav';
import { computed, signal } from '~/lib/signal';
import { appConfigService } from './app-config';
import {
  beginAuth,
  buildLogoutUrl,
  clearTokens,
  completeAuth,
  decodeJwt,
  getAccessToken,
  msUntilExpiry,
  refreshTokens,
  rolesFromClaims,
} from './oidc';
import { staffApiService } from './staff/staff-api.service';
import { onUnauthorized } from './ticketing/ticketing.client';
import type { WhoAmIResponse, WireTicketVisibility } from './staff/staff.types';
import type { CurrentUser, PermissionAction, Role, TicketScope } from './models';

/** Role → granted permissions (fallback when WhoAmI is unavailable).
 *  'Admin' is all-powerful (handled via the '*' wildcard in can()). */
const ROLE_PERMISSIONS: Record<Role, PermissionAction[]> = {
  Admin: [],
  Moderator: [
    'tickets.read.all',
    'tickets.write.all',
    'tickets.assign',
    'tickets.archive',
    'presence.hide',
    'staff.invite',
    'staff.manage',
    'products.read',
    'knowledge.read',
    'knowledge.manage',
  ],
  Staff: ['tickets.read.assigned', 'tickets.write.assigned', 'products.read', 'knowledge.read'],
};

const MOCK_USER: CurrentUser = {
  id: 'u_mock_admin',
  name: 'Mira Castellano',
  email: 'mira@lumendesk.io',
  role: 'Admin',
};

/** Synchronous mirror of the session signals (see header). */
interface SessionNow {
  user: CurrentUser | null;
  perms: ReadonlySet<string>;
  superadmin: boolean;
  sessionExpired: boolean;
}

export class AuthService {
  private readonly appConfig = appConfigService;
  private readonly staffApi = staffApiService;
  private readonly now: SessionNow = { user: null, perms: new Set(), superadmin: false, sessionExpired: false };

  private readonly _user = signal<CurrentUser | null>(null);
  private readonly _perms = signal<ReadonlySet<string>>(new Set());
  private readonly _superadmin = signal(false);
  private readonly _scope = signal<TicketScope>('none');
  private readonly _aiEnabled = signal(false);
  private readonly _idleAwayMinutes = signal(DEFAULT_IDLE_AWAY_MINUTES);
  private readonly _managedLogin = signal(false);
  private readonly _require2fa = signal(false);

  /** Pending proactive token-refresh timer (oidc mode only). */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    onUnauthorized(() => this.markSessionExpired());
    // oidc access tokens are short-lived (~5 min); refresh proactively (below)
    // AND when a backgrounded tab is refocused near/after expiry, so an idle tab
    // doesn't bounce to /login on the next action.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (
          document.visibilityState === 'visible' &&
          this.appConfig.snapshot.authMode === 'oidc' &&
          this.now.user !== null &&
          msUntilExpiry() < 60_000
        ) {
          void this.refreshNow();
        }
      });
    }
  }

  readonly currentUser = this._user.asReadonly();
  readonly authed = computed(() => this._user() !== null);
  readonly role = computed<Role | null>(() => this._user()?.role ?? null);
  /** Ticket visibility tier (WhoAmI.ticket_scope) — drives "assigned to me" scoping. */
  readonly ticketScope = this._scope.asReadonly();
  /** Whether AI assist is available for this caller/tenant (WhoAmI.ai_enabled). */
  readonly aiEnabled = this._aiEnabled.asReadonly();
  /** Tenant idle-away window (minutes) — drives the presence idle timer. */
  readonly idleAwayMinutes = this._idleAwayMinutes.asReadonly();
  /** Whether this workspace runs platform-managed sign-in (WhoAmI.managed_login) —
   *  gates the managed team-management UI (invite emails a set-password link,
   *  reset-password action). */
  readonly managedLogin = this._managedLogin.asReadonly();
  /** Whether the tenant requires 2FA for all staff (WhoAmI.require_2fa) — drives the
   *  Settings → Security toggle's current state (managed login only). */
  readonly require2fa = this._require2fa.asReadonly();
  /** Platform superadmin (control-plane access). */
  readonly superadmin = this._superadmin.asReadonly();
  /** Tenant top-level administrator: a platform superadmin OR a staff member holding
   *  the '*' wildcard (the built-in Administrator role). Mirrors the service-side
   *  top-level gate, so it gates high-privilege affordances like changing the console
   *  sign-in, which a settings.manage-only custom role must not see. */
  readonly isAdmin = computed(() => this._superadmin() || this._perms().has('*'));

  /** Authenticated, but the tenant granted ZERO effective permissions — a user
   *  who signed in via OIDC and was auto-provisioned as a role-less staff member.
   *  Such a user would otherwise land on an empty, broken shell; instead we route
   *  them to a "your account isn't assigned a role yet" page. Safe to gate on an
   *  empty set: the role-fallback path (WhoAmI unreachable) always grants at least
   *  the Staff permissions, so an empty set means WhoAmI authoritatively reported
   *  nothing. Superadmins and anyone with even one permission are unaffected. */
  readonly noAccess = computed(() => this.authed() && !this._superadmin() && this._perms().size === 0);

  /** Hydrate the session at bootstrap (called from provideAppInitializer). */
  async loadSession(): Promise<void> {
    const mode = this.appConfig.snapshot.authMode;
    if (mode === 'mock') {
      this.applyMock();
      return;
    }
    if (mode === 'bff') {
      await this.loadBffSession();
      // F53: force the BFF to re-mint the access token on bootstrap so a role /
      // permission change made while the user was signed in applies on a plain page
      // reload (the roles ride on the access token). Best-effort + before WhoAmI.
      if (this.now.user) await this.forceBffRefresh();
    } else {
      this.loadOidcSession();
      if (this.now.user) this.scheduleTokenRefresh();
    }
    // Enrich an established session with tenant authorization from WhoAmI.
    if (this.now.user) await this.enrichFromWhoAmI();
  }

  /** Begin sign-in for the current mode. `returnTo` is the in-app path to land on
   *  after sign-in (a captured deep link); defaults to home. */
  login(returnTo = '/'): void {
    this.setSessionExpired(false);
    const cfg = this.appConfig.snapshot;
    if (cfg.authMode === 'mock') {
      this.applyMock();
      navigate(returnTo);
      return;
    }
    if (cfg.authMode === 'bff') {
      // BFF (managed login) deep-link return would need the sidecar to carry the path
      // through /bff/callback; not wired yet, so this lands on home. TODO: thread
      // returnTo through the BFF login round trip.
      window.location.href = cfg.bff.login;
      return;
    }
    // oidc: Authorization-Code + PKCE — beginAuth persists returnTo so the callback
    // (handleOidcCallback → returnTo) navigates back to the originally-requested route.
    void beginAuth(cfg.oidc, returnTo).then((url) => window.location.assign(url));
  }

  /**
   * Complete the OIDC redirect: exchange the authorization code (PKCE) for
   * tokens, hydrate the session, and return the in-app path to navigate to.
   * Called by AuthCallbackComponent.
   */
  async handleOidcCallback(): Promise<string> {
    const cfg = this.appConfig.snapshot;
    if (cfg.authMode !== 'oidc') return '/';
    const result = await completeAuth(cfg.oidc, new URLSearchParams(window.location.search));
    if (!result.ok) {
      console.warn('[auth] oidc callback failed:', result.error);
      return '/login';
    }
    this.loadOidcSession();
    if (this.now.user) {
      this.scheduleTokenRefresh();
      await this.enrichFromWhoAmI();
    }
    return result.returnTo || '/';
  }

  logout(): void {
    const cfg = this.appConfig.snapshot;
    this.clear();
    if (cfg.authMode === 'bff') {
      window.location.href = cfg.bff.logout;
      return;
    }
    if (cfg.authMode === 'oidc') {
      const url = buildLogoutUrl(cfg.oidc);
      clearTokens();
      window.location.href = url;
      return;
    }
    navigate('/login');
  }

  /** True when a live session died mid-use; surfaces the "session expired" banner
   *  on the login screen instead of a silently broken page. */
  private readonly _sessionExpired = signal(false);
  readonly sessionExpired = this._sessionExpired.asReadonly();

  /** Called on a hard 401 (cookie/session expiry) by BOTH the HTTP interceptor and
   *  the fetch-based Connect client. Debounced so a burst of failing calls redirects
   *  only once; the user re-authenticates from the login screen. */
  markSessionExpired(): void {
    if (this.now.sessionExpired) return;
    this.setSessionExpired(true);
    this.clear();
    navigate('/login');
  }

  private setSessionExpired(v: boolean): void {
    this.now.sessionExpired = v;
    this._sessionExpired.set(v);
  }

  /** Permission check used to gate UI affordances and route guards. */
  can(action: PermissionAction): boolean {
    // Subscribe (reactive callers) but decide from the mirror (always current).
    this._superadmin();
    this._perms();
    if (this.now.superadmin) return true;
    const p = this.now.perms;
    return p.has('*') || p.has(action);
  }

  // --- session writes (signal + synchronous mirror) ---------------------------

  private setUser(u: CurrentUser | null): void {
    this.now.user = u;
    this._user.set(u);
  }

  private setPerms(p: ReadonlySet<string>): void {
    this.now.perms = p;
    this._perms.set(p);
  }

  private setSuperadmin(v: boolean): void {
    this.now.superadmin = v;
    this._superadmin.set(v);
  }

  // --- session shaping ------------------------------------------------------

  private applyMock(): void {
    this.setUser(MOCK_USER);
    this.setPerms(new Set(['*']));
    // The demo persona is a platform superadmin so the superadmin-only surfaces
    // (Platform: status + metering + billing) are visible in mock/demo mode.
    // bff/oidc still derive superadmin from WhoAmI.
    this.setSuperadmin(true);
    this._scope.set('all');
    this._aiEnabled.set(true);
  }

  private clear(): void {
    this.clearRefreshTimer();
    this.setUser(null);
    this.setPerms(new Set());
    this.setSuperadmin(false);
    this._scope.set('none');
    this._aiEnabled.set(false);
    this._idleAwayMinutes.set(DEFAULT_IDLE_AWAY_MINUTES);
    this._managedLogin.set(false);
    this._require2fa.set(false);
  }

  // --- oidc silent refresh --------------------------------------------------

  /** Arm a timer to refresh the access token shortly before it expires. */
  private scheduleTokenRefresh(): void {
    if (this.appConfig.snapshot.authMode !== 'oidc') return;
    this.clearRefreshTimer();
    // Refresh ~60s early; never sooner than 5s out (avoids a tight loop if a
    // freshly-minted token somehow reports a tiny TTL).
    const delay = Math.max(5_000, msUntilExpiry() - 60_000);
    this.refreshTimer = setTimeout(() => void this.refreshNow(), delay);
  }

  /** Refresh the access token now; reschedule on success, sign out on failure. */
  private async refreshNow(): Promise<void> {
    const cfg = this.appConfig.snapshot;
    if (cfg.authMode !== 'oidc') return;
    // A refresh only mints a new access token for the SAME identity, so we keep
    // the WhoAmI-refined user/permissions as-is and just rearm the timer. The
    // interceptor + Connect transport read the new token from sessionStorage.
    if (await refreshTokens(cfg.oidc)) {
      this.scheduleTokenRefresh();
    } else {
      this.markSessionExpired();
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Pull effective authorization from the live backend; fall back to the role map. */
  private async enrichFromWhoAmI(): Promise<void> {
    if (!this.appConfig.snapshot.ticketing.enabled) {
      this.applyRoleFallback();
      return;
    }
    try {
      this.applyWhoAmI(await this.staffApi.whoAmI());
    } catch {
      // WhoAmI unavailable (e.g. an open-mode backend doesn't mount StaffService).
      this.applyRoleFallback();
    }
  }

  private applyWhoAmI(me: WhoAmIResponse): void {
    const perms = new Set(me.permissions ?? []);
    this.setPerms(perms);
    this.setSuperadmin(!!me.superadmin);
    this._scope.set(scopeToUi(me.ticketScope));
    this._aiEnabled.set(!!me.aiEnabled);
    this._idleAwayMinutes.set(clampIdleAway(me.idleAwayMinutes));
    this._managedLogin.set(!!me.managedLogin);
    this._require2fa.set(!!me.require2fa);
    // Refine identity from the authoritative tenant record where present, and
    // reconcile the DISPLAYED role with effective authorization. The role label
    // shown in the footer/account menu comes from _user().role — which mapRole()
    // derived from IdP claims that may not carry a recognizable admin role name
    // (so an actual admin reads as "Staff"). WhoAmI is authoritative, so re-derive
    // the display role from it, matching how can() decides Admin.
    const u = this.now.user;
    if (u) {
      this.setUser({
        ...u,
        id: me.staff?.id ?? u.id,
        name: me.displayName || me.staff?.displayName || u.name,
        email: me.email || me.staff?.email || u.email,
        role: roleFromWhoAmI(me, perms),
      });
    }
  }

  private applyRoleFallback(): void {
    const role = this.now.user?.role ?? null;
    const perms = role ? (role === 'Admin' ? ['*'] : ROLE_PERMISSIONS[role]) : [];
    this.setPerms(new Set(perms));
    this.setSuperadmin(false);
    this._scope.set(role === 'Staff' ? 'assigned' : role ? 'all' : 'none');
    this._aiEnabled.set(false);
    this._idleAwayMinutes.set(DEFAULT_IDLE_AWAY_MINUTES);
    this._managedLogin.set(false);
    this._require2fa.set(false);
  }

  // --- mode-specific identity (bff/oidc) ------------------------------------

  private async loadBffSession(): Promise<void> {
    try {
      const res = await fetch(this.appConfig.snapshot.bff.userinfo, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) return;
      const info = (await res.json()) as Partial<CurrentUser> & { roles?: string[] };
      this.setUser({
        id: info.id ?? 'me',
        name: info.name ?? 'Agent',
        email: info.email ?? '',
        role: mapRole(info.roles ?? []),
      });
    } catch {
      /* unauthenticated — stay signed out */
    }
  }

  /** F53: ask the BFF to re-mint the access token (POST /bff/refresh) so a role or
   *  permission change made while the user was signed in applies on a plain reload
   *  (the roles ride on the access token, re-evaluated by Keycloak on refresh).
   *  Best-effort — failures are ignored, leaving the existing token in place. */
  private async forceBffRefresh(): Promise<void> {
    try {
      const url = this.appConfig.snapshot.bff.userinfo.replace(/\/userinfo$/, '/refresh');
      await fetch(url, { method: 'POST', credentials: 'include', cache: 'no-store' });
    } catch {
      /* best-effort — keep the current token */
    }
  }

  private loadOidcSession(): void {
    const token = getAccessToken();
    if (!token) return;
    const claims = decodeJwt(token);
    if (!claims) return;
    this.setUser({
      id: claims.sub ?? 'me',
      name: claims.name || claims.preferred_username || 'Agent',
      email: claims.email ?? '',
      role: mapRole(rolesFromClaims(claims)),
    });
  }
}

/** Derive the DISPLAY role from WhoAmI's effective authorization, so the role
 *  label matches what the caller can actually do (an IdP-governed admin whose
 *  claims lack a recognizable admin role name must still read as "Admin").
 *  Admin is decided exactly as can() does (superadmin OR the '*' wildcard), plus
 *  admin-equivalent capabilities (managing roles/settings). Moderator/Staff fall
 *  out of the perm set + ticket scope; otherwise defer to the IdP role names. */
function roleFromWhoAmI(me: WhoAmIResponse, perms: ReadonlySet<string>): Role {
  if (me.superadmin || perms.has('*') || perms.has('roles.manage') || perms.has('settings.manage')) {
    return 'Admin';
  }
  if (perms.has('staff.manage') || perms.has('tickets.read.all') || me.ticketScope === 'TICKET_VISIBILITY_ALL') {
    return 'Moderator';
  }
  if (perms.size > 0) return 'Staff';
  // No effective perms reported — fall back to the IdP role names.
  return mapRole(me.roles ?? []);
}

/** Map IdP role names → console Role (display + permission fallback). */
function mapRole(roles: string[]): Role {
  const lower = roles.map((r) => r.toLowerCase());
  if (lower.some((r) => r.includes('admin') || r.includes('superadmin'))) return 'Admin';
  if (lower.some((r) => r.includes('moderator') || r.includes('operator'))) return 'Moderator';
  return 'Staff';
}

function scopeToUi(s: WireTicketVisibility | undefined): TicketScope {
  switch (s) {
    case 'TICKET_VISIBILITY_ALL':
      return 'all';
    case 'TICKET_VISIBILITY_ASSIGNED':
      return 'assigned';
    default:
      return 'none';
  }
}

/** Tenant idle-away window bounds (mirror the service clamp) + the default. */
const DEFAULT_IDLE_AWAY_MINUTES = 10;
const MIN_IDLE_AWAY_MINUTES = 1;
const MAX_IDLE_AWAY_MINUTES = 120;

/** Clamp WhoAmI's idle-away minutes into the valid range; default when absent/0. */
function clampIdleAway(min: number | undefined): number {
  if (!min || !Number.isFinite(min)) return DEFAULT_IDLE_AWAY_MINUTES;
  return Math.min(MAX_IDLE_AWAY_MINUTES, Math.max(MIN_IDLE_AWAY_MINUTES, Math.round(min)));
}

export const authService = new AuthService();
